import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient, { redirectToAuth } from '../utils/api';
import { Button } from '../components/ui/button';
import { Star, MapPin, Calendar, ArrowLeft, Flag, Music, Mail, Copy, Check, X, MessageCircle, FileText } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useAuthStore } from '../store/authStore';
import SocialShare from '../components/SocialShare';
import FavoriteButton from '../components/FavoriteButton';
import SimilarArtists from '../components/SimilarArtists';
import MusicPlayer from '../components/MusicPlayer';
import { ArtistBadges } from '../components/VerificationBadges';
import { toast } from 'sonner';

export default function ArtistDetail() {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, isLoading } = useAuthStore();
  const [artist, setArtist] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [rider, setRider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  // Check auth status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await apiClient.get('/auth/me');
        // Auth store will be updated by interceptor if needed
      } catch (error) {
        // Not authenticated - that's okay for viewing artists
      } finally {
        setCheckingAuth(false);
      }
    };
    
    // Only check if auth store says we're loading
    if (isLoading) {
      checkAuth();
    } else {
      setCheckingAuth(false);
    }
  }, [isLoading]);

  const handleReport = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to report content');
      return;
    }
    if (!reportReason || !reportDescription) {
      toast.error('Please select a reason and provide details');
      return;
    }
    
    setSubmittingReport(true);
    try {
      await apiClient.post('/reports', {
        content_type: 'profile',
        content_id: artistId,
        reported_user_id: artist?.user_id,
        reason: reportReason,
        description: reportDescription
      });
      toast.success('Report submitted. Our team will review it shortly.');
      setShowReportModal(false);
      setReportReason('');
      setReportDescription('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmittingReport(false);
    }
  };

  useEffect(() => {
    fetchArtistData();
    // Track recently viewed (per-user list)
    if (isAuthenticated) {
      apiClient.post(`/recently-viewed/${artistId}`).catch(() => {});
    }
    // Track profile view for artist analytics — once per session per artist
    try {
      const seenKey = `artist_view_${artistId}`;
      if (!sessionStorage.getItem(seenKey)) {
        apiClient.post(`/artists/${artistId}/view`).catch(() => {});
        sessionStorage.setItem(seenKey, '1');
      }
    } catch (e) {
      // sessionStorage unavailable — fall back to a single fire-and-forget
      apiClient.post(`/artists/${artistId}/view`).catch(() => {});
    }
  }, [artistId, isAuthenticated]);

  const fetchArtistData = async () => {
    try {
      setLoading(true);
      // Fetch artist data first
      const artistRes = await apiClient.get(`/artists/${artistId}`);
      setArtist(artistRes.data);
      
      // Fetch reviews separately - don't fail if reviews fail
      try {
        const reviewsRes = await apiClient.get(`/reviews/artist/${artistId}`);
        setReviews(reviewsRes.data);
      } catch (reviewError) {
        setReviews([]);
      }
      // Fetch rider if available
      try {
        const riderRes = await apiClient.get(`/artists/${artistId}/rider`);
        if (riderRes.data && (riderRes.data.technical || riderRes.data.hospitality)) {
          setRider(riderRes.data);
        }
      } catch { }
    } catch (error) {
      console.error('Error fetching artist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookNow = () => {
    setShowBookingModal(true);
    setEmailCopied(false);
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText('bookings@razientsounds.com').then(() => {
      setEmailCopied(true);
      toast.success('Email copied to clipboard!');
      setTimeout(() => setEmailCopied(false), 3000);
    }).catch(() => {
      toast.error('Failed to copy email');
    });
  };

  const handleBookThroughUs = async () => {
    if (!isAuthenticated) {
      redirectToAuth(`/artist/${artistId}`);
      return;
    }
    try {
      const response = await apiClient.post(`/inquiries/artist/${artistId}`, {
        message: `Hi! I'm interested in booking you for an event.`
      });
      const { booking_id } = response.data;
      toast.success('Conversation started! Redirecting to chat...');
      setShowBookingModal(false);
      navigate(`/chat/${booking_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start conversation');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="glass-default rounded-2xl p-8 text-center">
          <p className="text-xl text-white/60">Artist not found</p>
          <Button onClick={() => navigate('/browse')} className="mt-4">
            Back to Browse
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative pt-20">
      <div className="container mx-auto px-4 py-8 relative z-10">

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Artist Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero Card */}
            <Card data-testid="artist-hero" className="glass-default border-white/10">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Artist Image */}
                  <div className="w-full md:w-64 h-64 rounded-2xl overflow-hidden bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 flex-shrink-0">
                    {artist.media_urls && artist.media_urls[0] ? (
                      <img
                        src={artist.media_urls[0]}
                        alt="Artist"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Star className="w-24 h-24 text-violet-400/50" />
                      </div>
                    )}
                  </div>

                  {/* Artist Details */}
                  <div className="flex-1 space-y-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h1 className="text-4xl font-bold" data-testid="artist-name-heading">{artist.stage_name || artist.name || artist.username || 'Artist'}</h1>
                          <ArtistBadges artist={artist} size="md" />
                        </div>
                        <p className="text-white/70 text-lg leading-relaxed">{artist.bio}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <FavoriteButton artistId={artist.artist_id} />
                        <SocialShare artistId={artist.artist_id} artistName={artist.stage_name || artist.name || artist.bio?.split(' ').slice(0, 3).join(' ')} />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowReportModal(true)}
                          className="text-white/40 hover:text-red-400 hover:bg-red-500/10"
                          title="Report this profile"
                        >
                          <Flag size={18} />
                        </Button>
                      </div>
                    </div>

                    {/* Genres */}
                    <div className="flex flex-wrap gap-2">
                      {artist.genres.map((genre, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 rounded-full bg-violet-500/20 text-violet-300 text-sm font-medium"
                        >
                          {genre}
                        </span>
                      ))}
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4 pt-4">
                      <div className="flex items-center gap-2">
                        {artist.rating > 0 ? (
                          <>
                            <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                            <span className="font-bold text-lg">{artist.rating.toFixed(1)}</span>
                            <span className="text-white/60 text-sm">({artist.total_bookings} bookings)</span>
                          </>
                        ) : (
                          <span className="text-white/60 text-sm">New artist</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-white/60">
                        <MapPin size={20} />
                        <span>{artist.is_local ? 'Local Artist' : 'Available for Travel'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Media Gallery */}
            {artist.media_urls && artist.media_urls.length > 1 && (
              <Card className="glass-default border-white/10">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold mb-4">Media Gallery</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {artist.media_urls.slice(1).map((url, idx) => (
                      <div
                        key={idx}
                        className="aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20"
                      >
                        <img
                          src={url}
                          alt={`Media ${idx + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Social Media Links */}
            {(artist.soundcloud_url || artist.instagram_url || artist.tiktok_url || artist.youtube_url || artist.spotify_url) && (
              <Card className="glass-default border-white/10" data-testid="artist-social-links">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold mb-4">Connect</h2>
                  <div className="flex flex-wrap gap-3">
                    {artist.soundcloud_url && (
                      <a href={artist.soundcloud_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/20 hover:bg-orange-500/30 text-orange-300 transition-colors"
                        data-testid="social-soundcloud-link">
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c-.009-.057-.05-.1-.1-.1m-.899.828c-.06 0-.091.037-.104.094L0 14.479l.165 1.308c.014.055.044.094.104.094s.09-.039.108-.094l.21-1.308-.21-1.334c-.018-.057-.047-.094-.108-.094m1.83-1.229c-.063 0-.112.05-.121.116l-.218 2.524.218 2.445c.009.066.058.116.121.116s.111-.05.12-.116l.248-2.445-.248-2.524c-.009-.066-.057-.116-.12-.116m.955-.405c-.074 0-.127.057-.133.128l-.206 2.929.206 2.8c.006.075.059.132.133.132.073 0 .126-.057.133-.132l.233-2.8-.233-2.929c-.007-.071-.06-.128-.133-.128m.965-.137c-.083 0-.139.064-.143.14l-.191 3.066.191 2.934c.004.08.06.144.143.144.079 0 .135-.064.143-.144l.217-2.934-.217-3.066c-.008-.076-.064-.14-.143-.14m1.015-.181c-.093 0-.152.071-.155.151l-.183 3.247.183 2.994c.003.089.062.151.155.151.087 0 .148-.062.155-.151l.202-2.994-.202-3.247c-.007-.08-.068-.151-.155-.151m.978-.236c-.1 0-.164.08-.166.162l-.163 3.483.163 3.044c.002.096.066.162.166.162.098 0 .162-.066.166-.162l.18-3.044-.18-3.483c-.004-.082-.066-.162-.166-.162m1.032-.302c-.113 0-.174.084-.177.171l-.155 3.785.155 3.058c.003.106.064.171.177.171.108 0 .172-.065.177-.171l.172-3.058-.172-3.785c-.005-.087-.069-.171-.177-.171m2.048.175c-.121 0-.195.098-.198.186l-.141 3.674.141 3.01c.003.113.077.186.198.186.117 0 .19-.073.198-.186l.152-3.01-.152-3.674c-.008-.088-.081-.186-.198-.186m-1.021-.258c-.111 0-.184.091-.188.178l-.148 3.756.148 3.03c.004.108.077.178.188.178.109 0 .182-.07.188-.178l.164-3.03-.164-3.756c-.006-.087-.079-.178-.188-.178m2.044-.2c-.132 0-.205.104-.209.196l-.13 3.773.13 3.003c.004.12.077.196.209.196.127 0 .201-.076.209-.196l.142-3.003-.142-3.773c-.008-.092-.082-.196-.209-.196m1.026-.187c-.14 0-.216.108-.22.204l-.119 3.792.119 2.977c.004.125.08.204.22.204.135 0 .213-.079.22-.204l.132-2.977-.132-3.792c-.007-.096-.085-.204-.22-.204m1.05.168c-.15 0-.229.119-.232.217l-.115 3.604.115 2.95c.003.132.082.217.232.217.146 0 .225-.085.232-.217l.123-2.95-.123-3.604c-.007-.098-.086-.217-.232-.217m1.09-.327c-.103 0-.226.126-.229.224l-.096 3.93.096 2.912c.003.138.126.224.229.224.158 0 .236-.086.236-.224l.105-2.912-.105-3.93c-.003-.098-.078-.224-.236-.224m4.668 1.139c-.158 0-.285.127-.285.285v6.163c0 .158.127.285.285.285h4.5c2.486 0 4.5-2.014 4.5-4.5s-2.014-4.233-4.5-4.233c-.772 0-1.5.197-2.133.544-.475-2.807-2.935-4.944-5.867-4.944-1.035 0-2.001.271-2.843.739-.252.141-.319.264-.322.524v8.795c.004.253.197.461.45.478h6.5"/></svg>
                        SoundCloud
                      </a>
                    )}
                    {artist.instagram_url && (
                      <a href={artist.instagram_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-pink-500/20 hover:bg-pink-500/30 text-pink-300 transition-colors"
                        data-testid="social-instagram-link">
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                        Instagram
                      </a>
                    )}
                    {artist.tiktok_url && (
                      <a href={artist.tiktok_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 transition-colors"
                        data-testid="social-tiktok-link">
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.78a8.18 8.18 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.21z"/></svg>
                        TikTok
                      </a>
                    )}
                    {artist.youtube_url && (
                      <a href={artist.youtube_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-colors"
                        data-testid="social-youtube-link">
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                        YouTube
                      </a>
                    )}
                    {artist.spotify_url && (
                      <a href={artist.spotify_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 hover:bg-green-500/30 text-green-300 transition-colors"
                        data-testid="social-spotify-link">
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
                        Spotify
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* SoundCloud Embed */}
            {artist.soundcloud_url && (
              <Card className="glass-default border-white/10" data-testid="soundcloud-embed">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold mb-4">Listen on SoundCloud</h2>
                  <iframe
                    title="SoundCloud Player"
                    width="100%"
                    height="166"
                    scrolling="no"
                    frameBorder="no"
                    allow="autoplay"
                    src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(artist.soundcloud_url)}&color=%238b5cf6&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false`}
                    className="rounded-lg"
                  />
                </CardContent>
              </Card>
            )}

            {/* Music Player */}
            {artist.audio_tracks && artist.audio_tracks.length > 0 && (
              <Card className="glass-default border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Music className="w-6 h-6 text-violet-400" />
                    <h2 className="text-2xl font-bold">Listen to Tracks</h2>
                  </div>
                  <MusicPlayer 
                    tracks={artist.audio_tracks} 
                    artistName={artist.stage_name || 'Artist'} 
                  />
                </CardContent>
              </Card>
            )}

            {/* Technical Rider */}
            {rider && (
              <Card className="glass-default border-white/10" data-testid="artist-rider-section">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-violet-400" /> Technical Rider
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    {rider.technical && Object.values(rider.technical).some(v => v) && (
                      <div>
                        <h3 className="font-semibold text-violet-300 mb-3">Technical Requirements</h3>
                        <div className="space-y-2 text-sm text-white/70">
                          {rider.technical.sound_requirements && <p><span className="text-white/50">Sound:</span> {rider.technical.sound_requirements}</p>}
                          {rider.technical.stage_requirements && <p><span className="text-white/50">Stage:</span> {rider.technical.stage_requirements}</p>}
                          {rider.technical.lighting_requirements && <p><span className="text-white/50">Lighting:</span> {rider.technical.lighting_requirements}</p>}
                          {rider.technical.dj_equipment && <p><span className="text-white/50">DJ Equipment:</span> {rider.technical.dj_equipment}</p>}
                          {rider.technical.monitors && <p><span className="text-white/50">Monitors:</span> {rider.technical.monitors}</p>}
                          {rider.technical.backline && <p><span className="text-white/50">Backline:</span> {rider.technical.backline}</p>}
                        </div>
                      </div>
                    )}
                    {rider.hospitality && Object.values(rider.hospitality).some(v => v) && (
                      <div>
                        <h3 className="font-semibold text-violet-300 mb-3">Hospitality</h3>
                        <div className="space-y-2 text-sm text-white/70">
                          {rider.hospitality.dressing_room && <p><span className="text-white/50">Dressing Room:</span> {rider.hospitality.dressing_room}</p>}
                          {rider.hospitality.catering && <p><span className="text-white/50">Catering:</span> {rider.hospitality.catering}</p>}
                          {rider.hospitality.beverages && <p><span className="text-white/50">Beverages:</span> {rider.hospitality.beverages}</p>}
                          {rider.hospitality.transportation && <p><span className="text-white/50">Transport:</span> {rider.hospitality.transportation}</p>}
                          {rider.hospitality.accommodation && <p><span className="text-white/50">Accommodation:</span> {rider.hospitality.accommodation}</p>}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Reviews */}
            <Card className="glass-default border-white/10">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-4">Reviews</h2>
                {reviews.length === 0 ? (
                  <div className="text-center py-8 text-white/40 border border-dashed border-white/10 rounded-lg" data-testid="reviews-empty-state">
                    <Star className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No reviews yet — be the first to book and share your experience!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div key={review.review_id} className="glass-light rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              size={16}
                              className={i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-white/20'}
                            />
                          ))}
                          <span className="text-sm text-white/60">
                            {new Date(review.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-white/80">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Booking Card */}
          <div className="lg:col-span-1">
            <Card data-testid="booking-card" className="glass-heavy border-white/10 sticky top-8">
              <CardContent className="p-6 space-y-6">
                <div>
                  <h3 className="text-2xl font-bold mb-2">Book This Artist</h3>
                  <p className="text-white/50 text-sm">Get in touch to discuss availability and details</p>
                </div>

                {/* Availability */}
                <div className="flex items-center gap-2 text-sm text-green-400 bg-green-500/10 rounded-lg p-3">
                  <Calendar size={16} />
                  <span className="capitalize">{artist.availability}</span>
                </div>

                {/* Book Button */}
                <Button
                  data-testid="book-now-button"
                  onClick={handleBookNow}
                  className="w-full h-14 text-lg font-bold rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 shadow-[0_0_20px_rgba(139,92,246,0.4)] hover:shadow-[0_0_30px_rgba(139,92,246,0.6)] transition-all"
                >
                  <Mail className="mr-2" size={20} />
                  Book Now
                </Button>

                <p className="text-xs text-white/50 text-center">
                  Message directly or contact via email for bookings
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Similar Artists */}
        <SimilarArtists artistId={artistId} />
      </div>

      {/* Booking Email Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowBookingModal(false)}>
          <div 
            className="relative w-full max-w-sm rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="booking-email-modal"
          >
            {/* Background with spinning planets */}
            <div className="absolute inset-0 bg-[#0d0d1a]" />
            
            {/* Stars */}
            <div className="absolute inset-0 overflow-hidden">
              {Array.from({ length: 30 }).map((_, i) => (
                <div
                  key={i}
                  className="absolute rounded-full bg-white"
                  style={{
                    width: Math.random() * 2 + 1 + 'px',
                    height: Math.random() * 2 + 1 + 'px',
                    left: Math.random() * 100 + '%',
                    top: Math.random() * 100 + '%',
                    opacity: Math.random() * 0.7 + 0.3,
                    animation: `twinkle ${Math.random() * 3 + 2}s ease-in-out infinite ${Math.random() * 2}s`
                  }}
                />
              ))}
            </div>

            {/* Spinning Planet 1 - Large purple */}
            <div 
              className="absolute w-16 h-16 rounded-full"
              style={{
                background: 'radial-gradient(circle at 35% 35%, #a855f7, #6d28d9, #3b0764)',
                boxShadow: '0 0 30px rgba(168, 85, 247, 0.4), inset -3px -3px 8px rgba(0,0,0,0.4)',
                top: '15%',
                right: '10%',
                animation: 'spinPlanet1 20s linear infinite'
              }}
            >
              {/* Ring */}
              <div 
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-3 rounded-full border border-violet-400/30"
                style={{ transform: 'translate(-50%, -50%) rotateX(70deg)' }}
              />
            </div>

            {/* Spinning Planet 2 - Small pink */}
            <div 
              className="absolute w-8 h-8 rounded-full"
              style={{
                background: 'radial-gradient(circle at 35% 35%, #ec4899, #be185d, #831843)',
                boxShadow: '0 0 15px rgba(236, 72, 153, 0.3), inset -2px -2px 5px rgba(0,0,0,0.4)',
                bottom: '20%',
                left: '8%',
                animation: 'spinPlanet2 15s linear infinite'
              }}
            />

            {/* Spinning Planet 3 - Medium blue */}
            <div 
              className="absolute w-10 h-10 rounded-full"
              style={{
                background: 'radial-gradient(circle at 35% 35%, #60a5fa, #2563eb, #1e3a5f)',
                boxShadow: '0 0 20px rgba(96, 165, 250, 0.3), inset -2px -2px 6px rgba(0,0,0,0.4)',
                top: '60%',
                right: '15%',
                animation: 'spinPlanet3 25s linear infinite'
              }}
            />

            {/* Content */}
            <div className="relative z-10 p-8 text-center">
              {/* Close button */}
              <button 
                onClick={() => setShowBookingModal(false)}
                className="absolute top-3 right-3 text-white/40 hover:text-white transition-colors"
                data-testid="close-booking-modal"
              >
                <X size={20} />
              </button>

              {/* Mail icon with glow */}
              <div className="flex justify-center mb-5">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{
                    background: 'radial-gradient(circle, rgba(168,85,247,0.3), rgba(168,85,247,0.05))',
                    boxShadow: '0 0 40px rgba(168, 85, 247, 0.3), 0 0 80px rgba(168, 85, 247, 0.15)'
                  }}
                >
                  <Mail className="w-8 h-8 text-violet-300" style={{ filter: 'drop-shadow(0 0 8px rgba(168, 85, 247, 0.8))' }} />
                </div>
              </div>

              <h3 className="text-xl font-bold text-white mb-2">Book This Artist</h3>
              <p className="text-white/50 text-sm mb-6">Choose how you'd like to connect</p>

              {/* Book Through Us - Direct Message */}
              <Button
                onClick={handleBookThroughUs}
                data-testid="book-through-us-btn"
                className="w-full h-12 rounded-full font-semibold mb-3 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 shadow-[0_0_20px_rgba(139,92,246,0.4)] transition-all"
              >
                <MessageCircle className="mr-2" size={18} />
                Book Through Us
              </Button>
              <p className="text-white/40 text-xs mb-5 text-center">Message the artist directly on RazientSounds</p>

              <div className="relative mb-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-3 bg-[#0d0d1a] text-white/40">or contact via email</span>
                </div>
              </div>

              {/* Email with purple glow */}
              <div 
                className="relative rounded-xl p-4 mb-4"
                style={{
                  background: 'rgba(168, 85, 247, 0.08)',
                  border: '1px solid rgba(168, 85, 247, 0.25)',
                  boxShadow: '0 0 20px rgba(168, 85, 247, 0.15), inset 0 0 20px rgba(168, 85, 247, 0.05)'
                }}
              >
                <p 
                  className="text-lg font-semibold tracking-wide"
                  style={{ 
                    color: '#c084fc',
                    textShadow: '0 0 10px rgba(192, 132, 252, 0.6), 0 0 20px rgba(168, 85, 247, 0.4), 0 0 40px rgba(139, 92, 246, 0.2)'
                  }}
                  data-testid="booking-email-text"
                >
                  bookings@razientsounds.com
                </p>
              </div>

              {/* Copy button */}
              <Button
                onClick={handleCopyEmail}
                data-testid="copy-email-btn"
                className={`w-full h-12 rounded-full font-semibold transition-all duration-300 ${
                  emailCopied 
                    ? 'bg-green-600 hover:bg-green-500 shadow-[0_0_20px_rgba(34,197,94,0.4)]' 
                    : 'bg-white/10 hover:bg-white/15 border border-white/20'
                }`}
              >
                {emailCopied ? (
                  <>
                    <Check className="mr-2" size={18} />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="mr-2" size={18} />
                    Copy Email Address
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* CSS animations for planets */}
          <style>{`
            @keyframes spinPlanet1 {
              0% { transform: rotate(0deg) translateX(10px) rotate(0deg); }
              100% { transform: rotate(360deg) translateX(10px) rotate(-360deg); }
            }
            @keyframes spinPlanet2 {
              0% { transform: rotate(0deg) translateY(8px) rotate(0deg); }
              100% { transform: rotate(-360deg) translateY(8px) rotate(360deg); }
            }
            @keyframes spinPlanet3 {
              0% { transform: rotate(0deg) translateX(-6px) translateY(6px) rotate(0deg); }
              100% { transform: rotate(360deg) translateX(-6px) translateY(6px) rotate(-360deg); }
            }
            @keyframes twinkle {
              0%, 100% { opacity: 0.3; }
              50% { opacity: 1; }
            }
          `}</style>
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="glass-default border-white/10 w-full max-w-md">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                  <Flag className="text-red-400" size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Report Profile</h2>
                  <p className="text-sm text-white/60">Help us keep the community safe</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm text-white/60 mb-2 block">Reason for report *</label>
                  <select
                    value={reportReason}
                    onChange={(e) => setReportReason(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white focus:border-violet-500 outline-none"
                  >
                    <option value="">Select a reason...</option>
                    <option value="inappropriate_content">Inappropriate Content</option>
                    <option value="fake">Fake or Misleading Profile</option>
                    <option value="spam">Spam or Scam</option>
                    <option value="harassment">Harassment</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm text-white/60 mb-2 block">Please describe the issue *</label>
                  <textarea
                    value={reportDescription}
                    onChange={(e) => setReportDescription(e.target.value)}
                    placeholder="Provide details about why you are reporting this profile..."
                    rows={4}
                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder:text-white/30 focus:border-violet-500 outline-none"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => { setShowReportModal(false); setReportReason(''); setReportDescription(''); }}
                    className="flex-1 border-white/20"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleReport}
                    disabled={submittingReport || !reportReason || !reportDescription}
                    className="flex-1 bg-red-600 hover:bg-red-500"
                  >
                    {submittingReport ? 'Submitting...' : 'Submit Report'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
